import { useLingui } from '@lingui/react/macro';
import { FaqAccordion, type FaqItem } from '../faq-accordion';
import { DiamondGrid, diamondGlows } from '../primitives';

/**
 * One array for the section and the `FAQPage` markup, as markup the page doesn't show gets a rich
 * result pulled. Q2-Q4 are drafts awaiting the content owner's approval (TEC-3626).
 */
export function useFaqItems(): FaqItem[] {
	const { t } = useLingui();
	return [
		{
			question: t`What requirements does Worldcoo set for an NGO that wants to join our partner entities?`,
			answer: t`To join Worldcoo, an organisation has to meet all of the following:`,
			bullets: [
				t`Be recognised as a non-profit organisation.`,
				t`Issue donation certificates valid for tax relief.`,
				t`A track record of at least 3 years since it was constituted.`,
				t`An up-to-date, professional website.`,
				t`Descriptive annual reports on the website.`,
				t`Information about the board of directors or trustees.`,
				t`Publicly available contact details.`,
				t`A data protection policy that complies with current regulations.`,
				t`A presence on social media.`,
				t`Respect for the principles of the Charter of the United Nations.`,
			],
		},
		{
			question: t`How does the project selection process work?`,
			answer: t`Approved projects join our database, and each partner company chooses the ones it wants to fund according to its own values and social strategy. Selection is therefore made by the company, not by Worldcoo — our job is to make sure every project in the pool meets the transparency criteria.`,
		},
		{
			question: t`Once a project is funded, how are the funds raised managed?`,
			answer: t`Donations are collected by the partner company and settled to the NGO through Worldcoo on a regular cycle. You can follow what has been raised in real time from your NGO area, and once the project reaches its goal we transfer the amount and ask for the report showing how it was spent.`,
		},
		{
			question: t`Commitments of the beneficiary organisations`,
			answer: t`Beneficiary entities undertake to use the funds only for the funded project, to report on how they were spent and on the results achieved, and to provide photographs and updates so the people who donated can see the impact of their contribution.`,
		},
	];
}

export function Faq() {
	const { t } = useLingui();
	const items = useFaqItems();

	return (
		<section className="bg-linen relative overflow-hidden">
			<DiamondGrid
				color="var(--color-brand)"
				opacity={0.35}
				masks={[diamondGlows({ x: '6%', y: '0%' }, { x: '94%', y: '78%' })]}
			/>

			<div className="relative mx-auto flex max-w-[1440px] flex-col items-center gap-8 px-6 py-16 md:py-[104px]">
				<h2 className="text-center text-3xl leading-[1.3] font-medium tracking-[-0.02em] md:text-[2.5rem]">
					{t`Frequently asked questions`}
				</h2>

				<FaqAccordion items={items} />
			</div>
		</section>
	);
}
